import React from 'react';

// SVG for the background gears
const GearsIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 140 100"
    fill="currentColor"
  >
    <path d="M62.8,43.2c-0.4-2.8-0.7-5.5-1.1-8.3c-0.1-0.7-0.2-1.3-0.3-2c-1.3-8-6.1-14.9-12.9-19.3c-0.9-0.5-1.8-1-2.8-1.5 c-3.3-1.6-6.9-2.5-10.7-2.5c-11.4,0-21.3,7.2-24.9,17.5c-0.2,0.6-0.4,1.2-0.6,1.8c-2.4,7.2-1.5,15.2,2.5,21.5 c0.5,0.8,1.1,1.5,1.7,2.2l0,0c0.1,0.1,0.1,0.1,0.2,0.2c6.8,7.3,16.8,10,25.9,7.5c2.4-0.7,4.7-1.7,6.8-3c0.9-0.5,1.7-1.1,2.5-1.7 c2.8-2,5.2-4.5,6.8-7.5c0.8-1.4,1.4-2.9,1.8-4.5C62.9,46.1,62.9,44.7,62.8,43.2z M35.3,47.1c-6.6,0-12-5.4-12-12s5.4-12,12-12 s12,5.4,12,12S41.9,47.1,35.3,47.1z" />
    <path d="M136.2,80.1c-0.2-1.4-0.4-2.8-0.5-4.2c-0.1-0.9-0.1-1.8-0.2-2.7c-0.8-5.1-3.5-9.7-7.4-12.9c-0.5-0.4-1-0.8-1.5-1.1 c-2.1-1.2-4.3-2-6.7-2.3c-8.2-1.2-16.1,2.3-20.2,9.2c-0.1,0.2-0.2,0.4-0.3,0.6c-4.6,7.7-3.4,17.4,2.9,23.3c0.8,0.7,1.6,1.4,2.5,2 l0,0c7.8,6.2,18.4,7.1,27.3,2.2c1.5-0.8,2.9-1.8,4.3-2.8c0.7-0.5,1.4-1.1,2-1.7c3.4-3.1,5.6-7.2,6.2-11.8 C136.1,82.3,136.2,81.2,136.2,80.1z M108.9,86.9c-4.8,0-8.7-3.9-8.7-8.7s3.9-8.7,8.7-8.7s8.7,3.9,8.7,8.7S113.7,86.9,108.9,86.9z" />
  </svg>
);

// SVG for the curved arrow pointing to the step number
const StepArrow = ({ className }) => (
  <svg
    className={className}
    width="67"
    height="42"
    viewBox="0 0 67 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 40C12.4167 36.3333 33.8 24.2 38 2"
      stroke="#DBEAFE" // Updated to blue-200
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M30 2H38L37 11"
      stroke="#DBEAFE" // Updated to blue-200
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function HowItWorksSection() {
  // We use JSX directly in the description to handle styled text easily
  const steps = [
    {
      number: '01',
      description: <>Register on the platform and get a <span className="font-bold text-cyan-500">free 14-day trial</span></>,
    },
    {
      number: '02',
      description: <>Configure the <span className="font-bold text-cyan-500">AI employee</span> and test its performance</>,
    },
    {
      number: '03',
      description: <>Select a <span className="font-bold text-cyan-500">plan</span>, connect all available services</>,
    },
    {
      number: '04',
      description: <>Train your AI employee with the skills you need</>,
    },
    {
      number: '05',
      description: <>Receive monthly <span className="font-bold text-cyan-500">conversion stats</span> from inquiries to sales</>,
    },
    {
      number: '06',
      description: <>The AI employee works 24/7, <span className="font-bold text-cyan-500">saving you time, money, and the stress</span> of managing human error</>,
    },
  ];

  return (
    // Use a wrapper with padding to create space for the skewed element
    <section className="py-12 sm:py-20 bg-slate-50">
      {/* Skewed background container */}
      <div className="relative bg-slate-800 transform -skew-y-2">
        {/* Un-skew the content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transform skew-y-2">
          {/* Background decorative gears */}
          <GearsIcon className="absolute top-4 right-4 md:right-8 w-32 h-32 sm:w-48 sm:h-48 text-white/5 opacity-80" />

          <div className="py-16 sm:py-24">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white text-center mb-16">
              How it works
            </h2>

            {/* White card containing the steps */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12">
              {/* Grid for the steps */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {steps.map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* Conditionally render arrow for steps 2, 4, 6 */}
                    {index % 2 !== 0 && (
                       <StepArrow className="absolute -top-10 -left-6 sm:-left-8 h-12 w-12" />
                    )}
                    <div className="inline-block px-3 py-1 border-2 border-blue-500 rounded-md mb-4">
                      <span className="text-xl font-bold text-blue-500">{step.number}</span>
                    </div>
                    <p className="text-slate-600 text-lg leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorksSection;
